import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { fetchPackages, searchPackages } from '../api';
import { navigateTo } from '../router';
import type { PackageSearchResult, PackageSummary } from '../types';
import { singleLanguageDotClass } from '../utils';
import { PackageRow } from './PackageRow';
import type { PackageGroup } from './PackageRow';

type SortKey = 'name' | 'recent' | 'quality' | 'relevance';

interface FilterStat {
  key: string;
  label: string;
  packages: number;
}

// Filters are derived from the catalog's languages at render time, so newly
// indexed ecosystems (Rust, Java, Swift, …) surface a row automatically.
// 'js-ts' merges javascript + typescript, matching how users think of them.
type PackageFilter = string;

const FILTER_LABELS: Record<string, string> = {
  all: 'All',
  'js-ts': 'JS / TS',
  python: 'Python',
  dart: 'Dart',
  rust: 'Rust',
  java: 'Java',
  kotlin: 'Kotlin',
  go: 'Go',
  swift: 'Swift',
  csharp: 'C#',
  cpp: 'C++',
  ruby: 'Ruby',
  php: 'PHP',
};

function filterLabel(filter: PackageFilter): string {
  return FILTER_LABELS[filter] ?? filter.charAt(0).toUpperCase() + filter.slice(1);
}

function matchesPackageFilter(pkg: PackageSummary, filter: PackageFilter): boolean {
  if (filter === 'all') return true;
  const languages = pkg.languages ?? [];
  if (filter === 'js-ts')
    return languages.includes('javascript') || languages.includes('typescript');
  return languages.includes(filter);
}

function groupPackages(packages: PackageSummary[]): PackageGroup[] {
  const map = new Map<string, PackageGroup>();
  for (const pkg of packages) {
    const key = `${pkg.registry}:${pkg.name}`;
    let group = map.get(key);
    if (!group) {
      group = {
        registry: pkg.registry,
        name: pkg.name,
        versions: [],
        aliases: pkg.aliases ?? [],
        latestVersion: pkg.version,
        displayVersion: pkg.display_version ?? pkg.version,
        indexedAt: pkg.indexed_at,
        releasedAt: pkg.released_at ?? null,
        quality: pkg.index_quality ?? null,
        qualityNote: pkg.index_quality_note ?? null,
        summary: pkg.summary ?? null,
        languages: pkg.languages ?? [],
      };
      map.set(key, group);
    }
    group.versions.push(pkg);
  }
  for (const group of map.values()) {
    group.versions.sort(
      (a, b) => new Date(b.indexed_at).getTime() - new Date(a.indexed_at).getTime()
    );
    const latest = group.versions[0];
    group.aliases = latest.aliases ?? [];
    group.latestVersion = latest.version;
    group.displayVersion = latest.display_version ?? latest.version;
    group.indexedAt = latest.indexed_at;
    group.releasedAt = latest.released_at ?? null;
    group.quality = latest.index_quality ?? null;
    group.qualityNote = latest.index_quality_note ?? null;
    group.summary = latest.summary ?? null;
    group.languages = latest.languages ?? [];
  }
  return Array.from(map.values());
}

/**
 * Sticky ecosystem rail. Replaces the wrapping pill row: a vertical list keeps
 * twelve-plus ecosystems on one axis, keeps the counts in an alignable column,
 * and lets the active state be a quiet tint instead of the only saturated fill
 * on the page. Below `lg` it wraps to as many rows as it needs — at this text
 * size that's two or three compact rows, which beats a scroll strip that hides
 * whichever ecosystems fall off the right edge.
 */
function EcosystemRail({
  stats,
  active,
  onSelect,
}: {
  stats: FilterStat[];
  active: PackageFilter;
  onSelect: (key: PackageFilter) => void;
}) {
  return (
    <nav
      aria-label="Filter by ecosystem"
      className="lg:sticky lg:top-[72px] lg:w-44 lg:shrink-0 lg:self-start"
    >
      <p className="eyebrow mb-2 hidden lg:block">Ecosystem</p>
      <ul className="flex flex-wrap gap-x-1 gap-y-1 lg:flex-col lg:flex-nowrap lg:gap-0">
        {stats.map((stat) => {
          const isActive = active === stat.key;
          return (
            <li key={stat.key} className="shrink-0">
              <button
                type="button"
                aria-current={isActive ? 'true' : undefined}
                onClick={() => onSelect(stat.key)}
                // The active marker follows the rail's axis: underline while
                // the items wrap in rows, left bar once it's a column.
                className={`flex w-full items-center gap-2 whitespace-nowrap border-b-2 px-2.5 py-1.5 text-xs transition-colors lg:border-b-0 lg:border-l-2 lg:px-2 ${
                  isActive
                    ? 'border-accent bg-accent-soft font-medium text-accent'
                    : 'border-transparent text-link hover:bg-accent-soft hover:text-accent'
                }`}
              >
                {stat.key !== 'all' && (
                  <span
                    aria-hidden="true"
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${singleLanguageDotClass(
                      stat.key === 'js-ts' ? 'typescript' : stat.key
                    )}`}
                  />
                )}
                <span className="truncate">{stat.label}</span>
                <span className="ml-auto pl-2 tabular-nums text-dim">{stat.packages}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function PackageCatalogSection({
  className = '',
  searchQuery,
}: {
  className?: string;
  searchQuery?: string;
}) {
  const [packages, setPackages] = useState<PackageSummary[]>([]);
  const [searchResult, setSearchResult] = useState<{
    query: string;
    matches: PackageSearchResult[];
  }>({ query: '', matches: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Which query failed, rather than a bare boolean: keyed this way the flag
  // invalidates itself as soon as the query changes, so there's no need to
  // reset it synchronously inside the search effect.
  const [failedQuery, setFailedQuery] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [packageFilter, setPackageFilter] = useState<PackageFilter>('all');
  // `null` means "follow the context": relevance while a query is live, name
  // otherwise. Picking from the dropdown pins a concrete key, so an explicit
  // choice survives typing and clearing the search box — which an effect that
  // reassigned sortKey on every query change would not.
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const normalizedSearchQuery = searchQuery?.trim() ?? '';
  const searchFailed = failedQuery !== null && failedQuery === normalizedSearchQuery;
  const isSearching =
    Boolean(normalizedSearchQuery) && !searchFailed && searchResult.query !== normalizedSearchQuery;
  const canRankByRelevance = Boolean(normalizedSearchQuery) && !searchFailed;
  const effectiveSortKey: SortKey =
    sortKey === null || (sortKey === 'relevance' && !canRankByRelevance)
      ? canRankByRelevance
        ? 'relevance'
        : 'name'
      : sortKey;

  // `isLoading` starts true and is re-armed by the Retry handler, so this
  // effect never has to set it synchronously — doing so would cascade an extra
  // render on every mount. The cancelled flag keeps a superseded fetch from
  // clearing the loading state belonging to a newer one.
  useEffect(() => {
    let cancelled = false;
    void fetchPackages()
      .then((pkgs) => {
        if (!cancelled) setPackages(pkgs);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setLoadError(err instanceof Error ? err.message : 'Unable to load the library catalog.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    if (!normalizedSearchQuery) return;
    let cancelled = false;
    // 50 is the server's hard ceiling (GET /api/packages/search clamps to
    // 1..50), so asking for more just misreports the intent.
    void searchPackages(normalizedSearchQuery, 50)
      .then((matches) => {
        if (!cancelled) setSearchResult({ query: normalizedSearchQuery, matches });
      })
      .catch(() => {
        if (cancelled) return;
        setFailedQuery(normalizedSearchQuery);
        setSearchResult((current) =>
          current.query === normalizedSearchQuery
            ? current
            : { query: normalizedSearchQuery, matches: [] }
        );
      });
    return () => {
      cancelled = true;
    };
  }, [normalizedSearchQuery]);

  const visiblePackages = useMemo(() => packages.filter((p) => !p.hidden), [packages]);

  // Similarity by `registry:name`, or null when no query is in play. Shared by
  // the rail counts and the list so both describe the same set of packages.
  const similarityByKey = useMemo(() => {
    if (!normalizedSearchQuery || searchFailed) return null;
    const matches = searchResult.query === normalizedSearchQuery ? searchResult.matches : [];
    const map = new Map<string, number>();
    for (const match of matches) {
      const key = `${match.registry}:${match.name}`;
      map.set(key, Math.max(map.get(key) ?? 0, match.similarity));
    }
    return map;
  }, [normalizedSearchQuery, searchFailed, searchResult]);

  // The pool the ecosystem counts are computed over. Previously this was always
  // the whole catalog, so a search for three packages still claimed
  // "Python 491" in the sidebar.
  const searchScopedPackages = useMemo(() => {
    if (!similarityByKey) return visiblePackages;
    return visiblePackages.filter((pkg) => similarityByKey.has(`${pkg.registry}:${pkg.name}`));
  }, [visiblePackages, similarityByKey]);

  const stats = useMemo<FilterStat[]>(() => {
    const allGroups = groupPackages(searchScopedPackages);

    // Collect every language present in the catalog; javascript/typescript
    // collapse into the single 'js-ts' entry.
    const langSet = new Set<string>();
    for (const group of allGroups) {
      for (const pkg of group.versions) {
        for (const lang of pkg.languages ?? []) langSet.add(lang);
      }
    }
    const filters: PackageFilter[] = ['all'];
    if (langSet.has('javascript') || langSet.has('typescript')) filters.push('js-ts');
    for (const lang of langSet) {
      if (lang === 'javascript' || lang === 'typescript') continue;
      filters.push(lang);
    }

    const buildStat = (filter: PackageFilter): FilterStat => ({
      key: filter,
      label: filterLabel(filter),
      packages: allGroups.filter((group) =>
        group.versions.some((pkg) => matchesPackageFilter(pkg, filter))
      ).length,
    });

    const [all, ...rest] = filters.map(buildStat).filter((s) => s.key === 'all' || s.packages > 0);
    rest.sort((a, b) => b.packages - a.packages || a.label.localeCompare(b.label));
    return [all, ...rest];
  }, [searchScopedPackages]);

  const groups = useMemo(() => {
    const filtered = searchScopedPackages.filter((pkg) => matchesPackageFilter(pkg, packageFilter));
    const grouped = groupPackages(filtered);

    if (effectiveSortKey === 'relevance' && similarityByKey) {
      return grouped.sort((a, b) => {
        const aScore = similarityByKey.get(`${a.registry}:${a.name}`) ?? 0;
        const bScore = similarityByKey.get(`${b.registry}:${b.name}`) ?? 0;
        if (bScore !== aScore) return bScore - aScore;
        return a.name.localeCompare(b.name);
      });
    }

    type Comparator = (a: PackageGroup, b: PackageGroup) => number;
    const byName: Comparator = (a, b) => a.name.localeCompare(b.name);
    const sorters: Record<Exclude<SortKey, 'relevance'>, Comparator> = {
      name: byName,
      recent: (a, b) => new Date(b.indexedAt).getTime() - new Date(a.indexedAt).getTime(),
      quality: (a, b) => (b.quality ?? -1) - (a.quality ?? -1) || byName(a, b),
    };
    return grouped.sort(effectiveSortKey === 'relevance' ? byName : sorters[effectiveSortKey]);
  }, [searchScopedPackages, packageFilter, similarityByKey, effectiveSortKey]);

  const PAGE_SIZE = 24;
  const [pageState, setPageState] = useState<{ key: string; count: number }>({
    key: '',
    count: PAGE_SIZE,
  });
  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasMoreRef = useRef(false);
  // Deliberately keyed on filter + query only, NOT sort. Re-sorting doesn't
  // change *which* packages are in scope, just their order, so the rendered
  // count stays meaningful. Adding sortKey here resets the count to 24 while
  // the reader is scrolled down, and the sentinel — still in view — instantly
  // refills to the same number: a ~165ms shrink-and-snap for no gain.
  const pageStateKey = `${packageFilter}:${normalizedSearchQuery}`;
  const visibleCount = pageState.key === pageStateKey ? pageState.count : PAGE_SIZE;
  const visibleGroups = groups.slice(0, visibleCount);
  const hasMore = visibleCount < groups.length;

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Infinite scroll via IntersectionObserver — created once, stable
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMoreRef.current) {
          setPageState((current) => {
            const currentCount = current.key === pageStateKey ? current.count : PAGE_SIZE;
            return { key: pageStateKey, count: currentCount + PAGE_SIZE };
          });
        }
      },
      { rootMargin: '400px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [pageStateKey]);

  const showRail = !isLoading && !loadError && stats.length > 1;

  return (
    <section className={`flex flex-col gap-5 lg:flex-row lg:gap-8 ${className}`.trim()}>
      {showRail && (
        <EcosystemRail stats={stats} active={packageFilter} onSelect={setPackageFilter} />
      )}

      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-2">
          <p className="text-sm text-dim">
            <span className="font-semibold text-ink">
              {groups.length.toLocaleString()} {normalizedSearchQuery ? 'matches' : 'libraries'}
            </span>
            {packageFilter !== 'all' && ` in ${filterLabel(packageFilter)}`}
          </p>
          <label className="inline-flex items-center gap-2 text-xs text-dim">
            <span className="text-2xs uppercase tracking-wide">Sort</span>
            <select
              value={effectiveSortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="border border-rule bg-panel px-2 py-1 text-xs text-ink outline-none transition-colors focus:border-accent"
            >
              {canRankByRelevance && <option value="relevance">Relevance</option>}
              <option value="name">Name (A–Z)</option>
              <option value="recent">Recently indexed</option>
              <option value="quality">Quality score</option>
            </select>
          </label>
        </div>

        {normalizedSearchQuery && searchFailed && (
          <p className="border border-rule bg-panel px-3 py-2 text-xs text-dim" role="status">
            Search is unavailable right now — showing the unfiltered catalog instead.
          </p>
        )}

        {isLoading || isSearching ? (
          <div className="lc-surface divide-y divide-rule-soft">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="h-[58px] animate-pulse bg-bg" />
            ))}
          </div>
        ) : loadError ? (
          <div
            className="border border-rule bg-panel px-6 py-12 text-center"
            data-testid="browse-load-error"
            role="alert"
          >
            <p className="text-sm font-medium text-ink">Couldn't load the library catalog.</p>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-dim">{loadError}</p>
            <Button
              type="button"
              // Re-arm the loading state here rather than in the fetch effect:
              // this is the event that actually starts a new attempt.
              onClick={() => {
                setIsLoading(true);
                setLoadError(null);
                setReloadKey((k) => k + 1);
              }}
              className="mt-5"
            >
              Retry
            </Button>
          </div>
        ) : groups.length === 0 ? (
          <div
            className="border border-dashed border-rule px-6 py-16 text-center"
            data-testid="browse-empty-state"
          >
            <p className="text-sm font-medium text-link">
              {normalizedSearchQuery
                ? `No libraries matched “${normalizedSearchQuery}”.`
                : 'No libraries found for the current filters.'}
            </p>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-dim">
              Every library is indexed on request. Tell us what you need and we'll pull it into the
              catalog.
            </p>
            <Button type="button" onClick={() => navigateTo('/request')} className="mt-5">
              Request a library →
            </Button>
          </div>
        ) : (
          <div className="lc-surface divide-y divide-rule-soft" data-testid="browse-package-grid">
            {visibleGroups.map((group) => (
              <PackageRow key={`${group.registry}:${group.name}`} group={group} />
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel — always in DOM so the [] observer effect can find it */}
        <div ref={sentinelRef} className="h-1" aria-hidden="true" />
        {hasMore && !isLoading && !isSearching && (
          <p className="text-center text-xs text-dim">Loading more…</p>
        )}
      </div>
    </section>
  );
}
